<template>
  <div class="wrap">
    <div class="nav">
      <span class="back" @click="$router.back()">&#8592;</span>
      <span class="title">填写车牌号</span>
    </div>

    <div class="card" v-if="loading">
      <div class="loading-text">加载中...</div>
    </div>

    <div class="card" v-else-if="error">
      <div class="error-text">{{ error }}</div>
      <button class="btn primary" @click="$router.back()">返回</button>
    </div>

    <template v-else>
      <!-- 车牌号输入 -->
      <div class="card">
        <div class="label">车牌号<span class="req">*</span></div>
        <div 
          class="input input-plate" 
          @click="openKeyboard"
        >
          <span v-if="form.plateNumber" class="plate-display">{{ form.plateNumber }}</span>
          <span v-else class="plate-placeholder">点击输入车牌号</span>
        </div>
        <div style="height:8px"></div>
        <div class="label">车牌颜色<span class="req">*</span></div>
        <select class="input" v-model="form.plateColor">
          <option 
            v-for="color in availablePlateColors" 
            :key="color.value" 
            :value="color.value"
          >
            {{ color.label }}
          </option>
        </select>
      </div>

      <!-- 车牌号键盘 -->
      <PlateKeyboard
        :visible="keyboardVisible"
        :value="form.plateNumber"
        :maxLength="8"
        @input="handlePlateInput"
        @close="handleKeyboardClose"
      />

      <div class="actions">
        <button 
          class="btn primary" 
          :disabled="submitting || !canSubmit" 
          @click="submit"
        >
          {{ submitting ? '提交中...' : '提交' }}
        </button>
        <button class="btn" @click="$router.back()">取消</button>
      </div>
    </template>
  </div>
</template>

<script>
import request from '@/utils/request'
import PlateKeyboard from '@/components/PlateKeyboard.vue'

export default {
  name: 'VehicleInvite',
  components: { PlateKeyboard },
  data () {
    return {
      orderId: '', // 从URL参数获取的订单ID
      loading: true,
      error: '',
      // 最近一次添加车牌后返回的订单信息（用于计算剩余可添加数量）
      latestOrder: null,
      // 最大可绑定车牌数（如果后端返回了 productDTO.maxBindPlateNumbers）
      maxBindPlateNumbers: null,
      // 还可添加的车牌数；null 表示未知（未返回上限）
      remainingPlateCount: null,
      form: {
        plateNumber: '',
        plateColor: 'UNKNOWN_COLOR'
      },
      submitting: false,
      keyboardVisible: false
    }
  },
  computed: {
    // 是否可以提交
    canSubmit () {
      // 只需要检查是否有订单ID和车牌号；如果已达上限（remainingPlateCount === 0）则禁用
      if (!this.orderId || !this.form.plateNumber || !this.form.plateNumber.trim()) return false
      if (this.remainingPlateCount === 0) return false
      return true
    },
    // 根据车牌号长度返回可用的车牌颜色选项（与 VehicleAdd.vue 保持一致）
    availablePlateColors () {
      const plateLength = this.form.plateNumber ? this.form.plateNumber.trim().length : 0
      
      // 所有颜色选项
      const allColors = [
        { value: 'UNKNOWN_COLOR', label: '未知' },
        { value: 'BLUE_WHITE_COLOR', label: '蓝底白字' },
        { value: 'YELLOW_BLACK_COLOR', label: '黄底黑字' },
        { value: 'WHITE_BLACK_COLOR', label: '白底黑字' },
        { value: 'BLACK_WHITE_COLOR', label: '黑底白字' },
        { value: 'GREEN_WHITE_COLOR', label: '绿底白字' },
        { value: 'GREEN_BLACK_COLOR', label: '绿底黑字' },
        { value: 'GREEN_YELLOW_BLACK_COLOR', label: '绿黄底黑字' }
      ]
      
      // 如果没有输入车牌号，返回所有选项
      if (plateLength === 0) {
        return allColors
      }
      
      // 7位车牌：只能选择"蓝底白字"和"黄底黑字"
      if (plateLength === 7) {
        return [
          { value: 'BLUE_WHITE_COLOR', label: '蓝底白字' },
          { value: 'YELLOW_BLACK_COLOR', label: '黄底黑字' }
        ]
      }
      
      // 8位车牌：只能选择"绿底黑字"和"绿黄底黑字"
      if (plateLength === 8) {
        return [
          { value: 'GREEN_BLACK_COLOR', label: '绿底黑字' },
          { value: 'GREEN_YELLOW_BLACK_COLOR', label: '绿黄底黑字' }
        ]
      }
      
      // 其他长度，返回所有选项
      return allColors
    }
  },
  async mounted () {
    // 参考优惠券二维码页面的方式，直接从 URL 参数获取 orderId
    // 不再使用 base64 编码的数据，简化二维码数据
    let orderId = this.$route.query.oid || this.$route.query.orderId
    
    // 兼容旧版本：如果有 data 参数，尝试从 base64 编码的数据中解析
    if (!orderId && this.$route.query.data) {
      try {
        const dataParam = this.$route.query.data
        const orderDataJson = decodeURIComponent(atob(dataParam))
        const orderData = JSON.parse(orderDataJson)
        orderId = orderData.o || orderData.orderId
        // 兼容旧版本：如果旧版本二维码包含 token，暂时保留（但不使用）
        if (orderData.t || orderData.authToken) {
          console.warn('[VehicleInvite] 检测到旧版本二维码（包含token），新版本已不再需要token')
        }
      } catch (e) {
        console.warn('[VehicleInvite] 尝试解析旧版本二维码数据失败:', e)
      }
    }
    
    if (!orderId) {
      this.error = '缺少订单ID参数，请重新扫描二维码'
      this.loading = false
      return
    }
    
    console.log('[VehicleInvite] 从URL参数获取订单ID:', orderId)
    
    // 设置订单ID，直接显示表单
    this.orderId = orderId
    this.loading = false
  },
  methods: {
    countValidPlates (plates) {
      const arr = Array.isArray(plates) ? plates : (plates ? [plates] : [])
      return arr.filter(p => {
        if (p == null) return false
        const s = String(p).trim()
        return s.length > 0
      }).length
    },
    // 从后端获取订单信息和产品信息
    async fetchOrderAndProductInfo (orderId, businessId) {
      try {
        // 使用免认证接口获取订单信息（用于扫码页面）
        const orderResp = await request({
          url: '/market/card/order/qr/info',
          method: 'get',
          params: { orderId: orderId },
          timeout: 30000
        })
        // 接口返回格式可能是 { data: { order: {...}, product: {...} } } 或 { data: {...} }
        const qrInfo = orderResp?.data || {}
        const orderData = qrInfo.order || qrInfo || orderResp?.data?.data || orderResp?.data || orderResp
        
        if (!orderData || (!orderData.orderId && !orderData.id && !orderData.order_id)) {
          this.error = '订单不存在或已删除'
          return
        }
        
        // 设置订单信息
        this.orderInfo = {
          orderId: orderData.orderId || orderData.id || orderData.order_id,
          id: orderData.orderId || orderData.id || orderData.order_id,
          order_id: orderData.orderId || orderData.id || orderData.order_id,
          productId: orderData.productId || orderData.product_id,
          product_id: orderData.productId || orderData.product_id,
          productName: orderData.productName || orderData.product_name || '月卡套餐',
          product_name: orderData.productName || orderData.product_name || '月卡套餐',
          plateNumbers: orderData.plateNumbers || orderData.plateNumberList || orderData.plateList || orderData.plates || [],
          plateNumberList: orderData.plateNumbers || orderData.plateNumberList || orderData.plateList || orderData.plates || [],
          plateList: orderData.plateNumbers || orderData.plateNumberList || orderData.plateList || orderData.plates || [],
          plates: orderData.plateNumbers || orderData.plateNumberList || orderData.plateList || orderData.plates || [],
          plateColors: orderData.plateColors || orderData.plate_colors || [],
          plate_colors: orderData.plateColors || orderData.plate_colors || [],
          businessId: businessId || orderData.businessId || orderData.business_id || null,
          business_id: businessId || orderData.businessId || orderData.business_id || null
        }
        
        // 如果接口返回了产品信息，直接使用；否则单独获取产品信息
        const productData = qrInfo.product || qrInfo.productInfo
        if (productData) {
          // 接口已经返回了产品信息，直接使用
          this.productInfo = productData
          // 从产品信息中提取限制信息
          this.maxBindPlateNumbers = this.productInfo.maxBindPlateNumbers
          this.bindBerthNumbers = this.productInfo.bindBerthNumbers
          
          // 计算还可以添加的车牌数量
          const currentBindCount = this.currentBindPlateCount
          this.remainingPlateCount = this.maxBindPlateNumbers != null 
            ? Math.max(0, this.maxBindPlateNumbers - currentBindCount) 
            : -1
          
          console.log('[VehicleInvite] 从接口返回的产品信息获取的限制信息:', {
            remainingPlateCount: this.remainingPlateCount,
            maxBindPlateNumbers: this.maxBindPlateNumbers,
            bindBerthNumbers: this.bindBerthNumbers,
            currentBindCount: currentBindCount
          })
        } else {
          // 接口没有返回产品信息，单独获取
          const productId = this.orderInfo.productId
          if (productId) {
            await this.fetchProductInfo(productId)
          } else {
            console.warn('[VehicleInvite] 订单中未找到产品ID，无法获取产品信息')
          }
        }
      } catch (e) {
        console.error('[VehicleInvite] 获取订单信息失败:', e)
        this.error = '获取订单信息失败，请稍后重试'
      }
    },
    // 获取产品信息
    async fetchProductInfo (productId) {
      try {
        const resp = await request({
          url: '/market/card/product/search',
          method: 'post',
          data: { productIds: [productId] },
          timeout: 30000 // 30秒超时
        })
        const products = (resp && (resp.data?.data || resp.data?.records || resp.data?.list || resp.data || [])) || []
        this.productInfo = Array.isArray(products) && products.length > 0 ? products[0] : null
        console.log('[VehicleInvite] 产品信息:', this.productInfo)
        
        // 从产品信息中提取限制信息
        if (this.productInfo) {
          this.maxBindPlateNumbers = this.productInfo.maxBindPlateNumbers
          this.bindBerthNumbers = this.productInfo.bindBerthNumbers
          
          // 计算还可以添加的车牌数量
          const currentBindCount = this.currentBindPlateCount
          this.remainingPlateCount = this.maxBindPlateNumbers != null 
            ? Math.max(0, this.maxBindPlateNumbers - currentBindCount) 
            : -1
          
          console.log('[VehicleInvite] 从产品信息获取的限制信息:', {
            remainingPlateCount: this.remainingPlateCount,
            maxBindPlateNumbers: this.maxBindPlateNumbers,
            bindBerthNumbers: this.bindBerthNumbers,
            currentBindCount: currentBindCount
          })
        }
      } catch (e) {
        console.error('[VehicleInvite] 获取产品信息失败:', e)
        // 获取产品信息失败不影响使用，只是无法显示支持泊位数
        // 超时或其他错误都不影响页面正常使用
        this.productInfo = null
        
        // 如果是超时错误，记录但不影响页面
        if (e?.code === 'ECONNABORTED' || e?.message?.includes('timeout')) {
          console.warn('[VehicleInvite] 获取产品信息超时，页面可正常使用，但无法显示支持泊位数限制')
        }
      }
    },
    // 智能识别车牌颜色（与营销端 Add.vue 保持一致）
    identifyPlateColor (plate) {
      if (!plate || plate.length < 6) {
        return 'UNKNOWN_COLOR'
      }

      const plateUpper = String(plate).trim().toUpperCase()

      // 新能源车牌识别规则（8位）
      if (plateUpper.length === 8) {
        // 8位车牌通常是新能源车牌，返回绿底黑字（与 Add.vue 保持一致）
        const prov = ['京','沪','粤','浙','苏','鲁','川','渝','津','冀','晋','蒙','辽','吉','黑','皖','闽','赣','豫','鄂','湘','桂','琼','贵','云','藏','陕','甘','青','宁','新']
        if (prov.some(p => plateUpper.startsWith(p))) {
          return 'GREEN_BLACK_COLOR' // 绿底黑字（与 Add.vue 保持一致）
        }
      }

      // 普通车牌识别规则（7位）
      if (plateUpper.length === 7) {
        // 7位车牌通常是普通车牌
        const prov = ['京','沪','粤','浙','苏','鲁','川','渝','津','冀','晋','蒙','辽','吉','黑','皖','闽','赣','豫','鄂','湘','桂','琼','贵','云','藏','陕','甘','青','宁','新']
        if (prov.some(p => plateUpper.startsWith(p))) {
          return 'BLUE_WHITE_COLOR' // 蓝底白字
        }
      }

      // 特殊车牌识别
      if (plateUpper.startsWith('WJ') || plateUpper.startsWith('军') || plateUpper.startsWith('海') ||
          plateUpper.startsWith('空') || plateUpper.startsWith('北') || plateUpper.startsWith('南') ||
          plateUpper.startsWith('东') || plateUpper.startsWith('西') || plateUpper.startsWith('中')) {
        return 'WHITE_BLACK_COLOR' // 白底黑字
      }

      // 港澳车牌识别
      if (plateUpper.startsWith('粤Z') || plateUpper.includes('港') || plateUpper.includes('澳')) {
        return 'BLACK_WHITE_COLOR' // 黑底白字
      }

      // 6位车牌识别（通常是新能源车牌）
      if (plateUpper.length === 6) {
        if (plateUpper.startsWith('粤Z') || plateUpper.includes('港') || plateUpper.includes('澳')) {
          return 'BLACK_WHITE_COLOR' // 黑底白字
        }
        return 'GREEN_WHITE_COLOR' // 绿底白字
      }

      // 默认返回蓝底白字（最常见的普通车牌）
      return 'BLUE_WHITE_COLOR'
    },
    // 打开键盘
    openKeyboard () {
      this.keyboardVisible = true
    },
    // 处理车牌号输入（与 VehicleAdd.vue 保持一致）
    handlePlateInput (value) {
      const oldPlateNumber = this.form.plateNumber
      this.form.plateNumber = value
      
      // 自动识别车牌颜色
      if (value && value.trim()) {
        const plateLength = value.trim().length
        const oldPlateLength = oldPlateNumber ? oldPlateNumber.trim().length : 0
        const availableColors = this.availablePlateColors
        // 检查当前颜色是否在可用选项中
        const currentColorValid = availableColors.some(c => c.value === this.form.plateColor)
        
        // 如果车牌号长度变化，或者当前颜色不在可用选项中，或者当前颜色是未知，则自动识别
        if (plateLength !== oldPlateLength || !currentColorValid || this.form.plateColor === 'UNKNOWN_COLOR') {
          // 自动识别并设置
          const identifiedColor = this.identifyPlateColor(value.trim().toUpperCase())
          // 检查识别出的颜色是否在可用选项中
          const isValidColor = availableColors.some(c => c.value === identifiedColor)
          
          if (isValidColor) {
            this.form.plateColor = identifiedColor
          } else {
            // 如果识别出的颜色不在可用选项中，使用第一个可用颜色
            if (availableColors.length > 0) {
              this.form.plateColor = availableColors[0].value
            }
          }
        }
        // 如果车牌号长度没变化且当前颜色在可用选项中，保持不变（不覆盖用户的手动选择）
      } else {
        // 如果清空了车牌号，重置为未知
        this.form.plateColor = 'UNKNOWN_COLOR'
      }
    },
    // 关闭键盘
    handleKeyboardClose () {
      this.keyboardVisible = false
    },
    // 获取商户ID（从订单中获取，或从二维码数据中获取）
    getBusinessIdFromOrder (order) {
      if (!order) return null
      // 尝试多种可能的字段名
      let businessId = order.businessId || order.business_id || null
      
      // 如果订单中没有 businessId，尝试从 businessContext 中获取
      if (!businessId) {
        try {
          const raw = sessionStorage.getItem('in_park_plates_payload')
          if (raw) {
            const payload = JSON.parse(raw)
            const businessContext = payload?.businessContext
            if (businessContext) {
              businessId = businessContext.businessId || businessContext.business_id || null
            }
          }
        } catch (e) {
          console.warn('[VehicleInvite] 从 businessContext 获取 businessId 失败:', e)
        }
      }
      
      return businessId
    },
    // 获取操作人ID（参考 Profile.vue 的实现）
    // 注意：扫码页面不需要用户登录，如果没有操作人ID则返回 null，后端会使用默认值
    getOperatorId () {
      // 从 store 的 userInfo 获取（如果用户已登录）
      if (this.$store && this.$store.getters) {
        const userInfo = this.$store.getters.userInfo
        if (userInfo) {
          const uid = userInfo.userId || userInfo.id
          if (uid) return uid
        }
        // 如果 userInfo 中没有，尝试从 getters 直接获取
        const userId = this.$store.getters.userId || this.$store.getters.user_id
        if (userId) return userId
      }
      
      // 再次从 localStorage 获取（与 Profile.vue 保持一致）
      const localStorageUserId = localStorage.getItem('userId')
      if (localStorageUserId) return localStorageUserId
      
      // 最后从当前 localStorage 的 token 解析（与 Profile.vue 保持一致）
      try {
        const token = localStorage.getItem('token') || ''
        const parts = (token || '').split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]))
          // 尝试多种可能的字段名（与 Profile.vue 保持一致）
          return payload.userId || payload.uid || payload.sub || payload.user_id || payload.id || null
        }
      } catch (e) {
        console.warn('[VehicleInvite] 从 token 解析 operatorId 失败:', e)
      }
      
      return null
    },
    // 获取操作人名称（用于生成月卡操作记录）
    // 注意：扫码页面不需要用户登录，如果没有操作人名称则返回空字符串，后端会使用 "system" 作为默认值
    // 注意：应该上传账号的名称，与个人中心页面"您好，xxxx"中的xxxx保持一致
    getOperatorName () {
      // 尝试从当前 localStorage 获取（如果用户已登录）
      try {
        const token = localStorage.getItem('token') || ''
        const parts = (token || '').split('.')
        if (parts.length === 3 && parts[1] && parts[1].trim()) {
          try {
            const payload = JSON.parse(atob(parts[1]))
            // 优先使用名称字段，不使用账号字段（username）
            return payload.realName || payload.name || payload.nickname || ''
          } catch (decodeError) {
            // base64 解码失败，忽略
          }
        }
      } catch (e) {
        // ignore
      }
      
      // 尝试从 Vuex store 获取（如果用户已登录）
      // 使用与 Profile.vue 中 displayName 相同的逻辑
      if (this.$store && this.$store.getters) {
        const realName = this.$store.getters.realName
        const nickname = this.$store.getters.nickname
        if (realName && realName.trim()) {
          return realName.trim()
        }
        if (nickname && nickname.trim()) {
          return nickname.trim()
        }
        // 如果 getters 中没有，从 userInfo 中获取名称
        const userInfo = this.$store.getters.userInfo
        if (userInfo) {
          const name = userInfo.realName || userInfo.nickname || userInfo.name || ''
          if (name && name.trim()) {
            return name.trim()
          }
        }
      }
      
      // 默认返回空字符串，后端会使用 "system" 作为默认值
      return ''
    },
    // 验证车牌号格式
    validatePlateNumber (plateNumber) {
      if (!plateNumber || !plateNumber.trim()) {
        return false
      }
      const trimmed = plateNumber.trim().toUpperCase()
      // 检查长度：应该是7位或8位
      if (trimmed.length !== 7 && trimmed.length !== 8) {
        return false
      }
      // 使用平台端的车牌号验证规则
      // 支持：省份简称 + A-Z + 6个A-Z0-9字符（共8个字符，新能源车牌）
      //      省份简称 + A-Z + 4个A-Z0-9字符 + 挂学警港澳（共7个字符）
      //      省份简称 + A-Z + 5个A-Z0-9字符（共7个字符，普通车牌）
      const plateRegex = /^[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼使领][A-Z]([A-Z0-9]{6}|[A-Z0-9]{4}[挂学警港澳]|[A-Z0-9]{5})$/
      return plateRegex.test(trimmed)
    },
    // 提交
    async submit () {
      if (!this.orderId) {
        alert('缺少订单ID，请重新扫描二维码')
        return
      }
      if (!this.form.plateNumber || !this.form.plateNumber.trim()) {
        alert('请输入车牌号')
        return
      }
      if (this.remainingPlateCount === 0) {
        alert('已达到最大绑定数量，无法继续添加')
        return
      }
      
      // 验证车牌号格式
      const plateNumber = this.form.plateNumber.trim().toUpperCase()
      if (!this.validatePlateNumber(plateNumber)) {
        alert('车牌号无效，无法添加车牌号。请输入正确的车牌号格式（7位或8位）')
        return
      }
      
      this.submitting = true
      try {
        // 车牌颜色：优先使用用户选择；为空/未知时兜底自动识别
        let plateColor = this.form.plateColor
        if (!plateColor || plateColor === 'UNKNOWN_COLOR') {
          plateColor = this.identifyPlateColor(plateNumber)
        }

        console.log('[VehicleInvite] 提交添加车牌请求:', { orderId: this.orderId, plateNumber: plateNumber, plateColor })
        
        // 使用免认证接口添加车牌（用于扫码页面，不需要token）
        // 接口：GET /market/business/card/order/add-plate-number
        // 接口参数：orderId（订单ID）、plateNumber（车牌号）、plateColor（车牌颜色）
        const addPlateResp = await request({ 
          url: '/market/business/card/order/add-plate-number', 
          method: 'get', 
          params: {
            orderId: this.orderId,
            plateNumber: plateNumber,
            plateColor: plateColor
          },
          timeout: 30000 // 30秒超时，避免网络慢时超时
        })
        
        // 接口返回更新后的订单信息
        const updatedOrder = addPlateResp?.data?.data || addPlateResp?.data || addPlateResp
        console.log('[VehicleInvite] 添加车牌成功，返回的订单信息:', updatedOrder)

        // 记录最新订单并尝试计算剩余可添加数量
        this.latestOrder = updatedOrder || null
        const maxBind = updatedOrder?.productDTO?.maxBindPlateNumbers
        if (maxBind != null && maxBind !== undefined && maxBind !== '') {
          const maxNum = Number(maxBind)
          if (!isNaN(maxNum)) {
            this.maxBindPlateNumbers = maxNum
            const currentCount = this.countValidPlates(updatedOrder?.plateNumbers || updatedOrder?.plateNumberList || updatedOrder?.plateList || updatedOrder?.plates || [])
            this.remainingPlateCount = Math.max(0, maxNum - currentCount)
          }
        }
        
        // 操作记录功能已移除，扫码页面不需要添加操作记录
        
        // 清空表单，可以继续填写
        this.form.plateNumber = ''
        this.form.plateColor = 'UNKNOWN_COLOR'

        if (this.remainingPlateCount === 0) {
          alert('添加成功，还可添加0个车牌号')
        } else if (this.remainingPlateCount != null) {
          alert(`添加成功，还可添加${this.remainingPlateCount}个车牌号`)
        } else {
          // 未返回上限时，仍提示成功
          alert('添加成功')
        }
      } catch (e) {
        console.error('[VehicleInvite] 提交失败:', e)
        let errorMsg = '提交失败，请稍后再试'
        
        // 处理不同类型的错误
        if (e?.code === 'ECONNABORTED' || e?.message?.includes('timeout')) {
          errorMsg = '请求超时，请检查网络连接后重试'
        } else if (e?.statusCode === 403 || e?.response?.status === 403) {
          errorMsg = e?.response?.data?.message || e?.message || '权限不足，无法添加车牌。请确认您有操作该订单的权限，或联系管理员。'
        } else if (e?.response?.status === 401) {
          errorMsg = '登录已过期，请重新登录'
        } else if (e?.response?.data?.message) {
          errorMsg = e.response.data.message
        } else if (e?.message) {
          errorMsg = e.message
        }
        
        alert(errorMsg)
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.wrap { 
  max-width: 430px; 
  margin: 0 auto; 
  background: #f7f8fa; 
  min-height: 100vh; 
  padding-bottom: 80px; 
}

.nav { 
  height: 50px; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  background: #fff; 
  box-shadow: 0 1px 0 #f0f0f0; 
  position: relative; 
}

.back { 
  position: absolute; 
  left: 0; 
  padding-left: 14px; 
  font-size: 22px; 
  cursor: pointer; 
}

.title { 
  font-size: 18px; 
  font-weight: bold; 
}

.card { 
  background: #fff; 
  border-radius: 12px; 
  margin: 14px 14px 0; 
  padding: 14px 14px; 
  box-shadow: 0 2px 8px #f0f0f0; 
}

.label { 
  font-size: 14px; 
  color: #888; 
  margin-bottom: 4px; 
}

.req { 
  color: #ff4444; 
  margin-left: 4px; 
}

.input { 
  width: 100%; 
  background: #f7f8fa; 
  border: none; 
  border-radius: 10px; 
  padding: 10px 12px; 
  font-size: 14px; 
  color: #222; 
  outline: none; 
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}

.input:focus {
  background: #fff;
  box-shadow: 0 0 0 2px rgba(35, 138, 255, 0.2);
}

select.input {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  padding-right: 36px;
  background-color: #f7f8fa;
}

select.input:focus {
  background-color: #fff;
}

select.input option {
  padding: 10px 12px;
  background: #fff;
  color: #222;
}

.input-plate {
  cursor: pointer;
  display: flex;
  align-items: center;
  min-height: 40px;
}

.plate-display {
  color: #222;
  font-weight: 500;
}

.plate-placeholder {
  color: #999;
}

.order-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
}

.order-label {
  font-size: 14px;
  color: #888;
}

.order-value {
  font-size: 14px;
  color: #222;
  font-weight: 500;
}

.order-warning {
  font-size: 13px;
  color: #ff4444;
  width: 100%;
  text-align: center;
  padding: 8px 0;
}

.order-hint {
  font-size: 13px;
  color: #52c41a;
  width: 100%;
  text-align: center;
  padding: 8px 0;
}

.loading-text,
.error-text {
  text-align: center;
  padding: 20px 0;
  font-size: 14px;
  color: #666;
}

.error-text {
  color: #ff4444;
  margin-bottom: 16px;
}

.actions { 
  display: flex; 
  gap: 12px; 
  margin: 18px 14px 0; 
}

.btn { 
  flex: 1; 
  background: #fff; 
  color: #888; 
  font-size: 16px; 
  font-weight: 600; 
  border: none; 
  border-radius: 12px; 
  padding: 10px 0; 
  box-shadow: 0 2px 8px #eee; 
  cursor: pointer;
}

.btn.primary { 
  background: #238aff; 
  color: #fff; 
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>

